/**
 * RevenueCat Configuration and Utilities
 *
 * This module handles all RevenueCat subscription management including:
 * - SDK initialization
 * - Customer identification
 * - Entitlement checking
 * - Purchase handling
 */

// Lazy-load RevenueCat SDK to prevent Stripe from loading on every page.
// The @revenuecat/purchases-js package transitively loads Stripe at module
// evaluation time. When Stripe is loaded globally, its iframes throw
// cross-origin script errors that crash Next.js's error overlay handler.
// Importing dynamically means Stripe only loads when a function in this
// module is actually called (e.g. when opening the paywall).
type Purchases = any;
let PurchasesPromise: Promise<any> | null = null;
async function loadPurchases(): Promise<any> {
  if (!PurchasesPromise) {
    PurchasesPromise = import('@revenuecat/purchases-js').then(m => m.Purchases);
  }
  return PurchasesPromise;
}

// Export types from RevenueCat (using any for simplicity)
export type CustomerInfo = any;
export type PurchasesOfferings = any;
export type PurchasesPackage = any;

// RevenueCat API Key (Web SDK)
// IMPORTANT: Set NEXT_PUBLIC_REVENUECAT_WEB_KEY in your environment
// Web Billing keys start with "rcb_". Secret keys (sk_) will NOT work with the Web SDK.
const REVENUECAT_API_KEY = process.env.NEXT_PUBLIC_REVENUECAT_WEB_KEY || process.env.NEXT_PUBLIC_REVENUECAT_API_KEY || '';

// Track if we already warned about the key
let keyWarningLogged = false;

// Check if API key is configured and valid for web
function ensureApiKey(): boolean {
  if (!REVENUECAT_API_KEY) {
    if (!keyWarningLogged) {
      console.warn('RevenueCat: No API key configured. Set NEXT_PUBLIC_REVENUECAT_WEB_KEY in environment.');
      keyWarningLogged = true;
    }
    return false;
  }
  if (REVENUECAT_API_KEY.startsWith('sk_')) {
    if (!keyWarningLogged) {
      console.warn(
        'RevenueCat: Secret key (sk_) detected — this will NOT work with the Web SDK.\n' +
        'The Web SDK requires a Web Billing key that starts with "rcb_".\n' +
        'To fix: Go to RevenueCat Dashboard > Project Settings > API Keys > Web Billing, ' +
        'copy the key, and set it as NEXT_PUBLIC_REVENUECAT_WEB_KEY in your environment.'
      );
      keyWarningLogged = true;
    }
    return false;
  }
  return true;
}

// Entitlement identifier
export const ENTITLEMENT_ID = 'OPMODE Pro';

// Product identifiers
export const PRODUCT_IDS = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
} as const;

// Map a RevenueCat product/store identifier to a plan type.
// Store identifiers vary by store and are NOT reliably substring-matchable:
//   Apple      -> '002' (monthly), '003' (yearly)   [opaque, no substring hint]
//   Play       -> 'com_opmode_monthly:opmode-pro-monthly' / ':opmode-pro-yearly'
//   Web Billing-> 'MonthlySub' / 'YearlySub'         [capitalised, breaks .includes('monthly')]
// so map the known ones explicitly, then fall back to case-insensitive
// substring matching (yearly checked first — Play's yearly id also contains
// the base subscription word "monthly").
const MONTHLY_PRODUCT_IDS = new Set([
  '002', 'monthlysub', 'com_opmode_monthly:opmode-pro-monthly', 'monthly', 'com.opmode.monthly',
]);
const YEARLY_PRODUCT_IDS = new Set([
  '003', 'yearlysub', 'com_opmode_monthly:opmode-pro-yearly', 'yearly', 'annual', 'com.opmode.annual',
]);

export function planTypeFromProductId(productId?: string | null): 'monthly' | 'yearly' | 'none' {
  if (!productId) return 'none';
  const id = productId.toLowerCase();
  if (YEARLY_PRODUCT_IDS.has(id)) return 'yearly';
  if (MONTHLY_PRODUCT_IDS.has(id)) return 'monthly';
  if (id.includes('year') || id.includes('annual')) return 'yearly';
  if (id.includes('month')) return 'monthly';
  return 'none';
}

let purchasesInstance: Purchases | null = null;

const NO_SUB = {
  isPro: false,
  isActive: false,
  willRenew: false,
  expirationDate: null,
  productIdentifier: null,
  isTrial: false,
};

const NO_DETAILS = {
  hasAccess: false,
  subscriptionType: 'none' as const,
  expirationDate: null,
  willRenew: false,
  isInTrial: false,
  managementURL: null,
};

/**
 * Initialize RevenueCat SDK
 * Must be called before any other RevenueCat operations
 * Returns null if the API key is missing or invalid (sk_ key on web)
 */
export async function initializeRevenueCat(userId?: string): Promise<Purchases | null> {
  if (purchasesInstance) {
    return purchasesInstance;
  }

  // Validate API key before proceeding
  if (!ensureApiKey()) {
    return null;
  }

  try {
    // Lazy-load the SDK module (and its Stripe dependency) only on first use
    const PurchasesClass = await loadPurchases();
    purchasesInstance = PurchasesClass.configure(
      REVENUECAT_API_KEY,
      userId || `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    );

    console.log('RevenueCat web SDK initialized successfully');

    return purchasesInstance;
  } catch (error) {
    console.error('Failed to initialize RevenueCat:', error);
    throw error;
  }
}

/**
 * Get the Purchases instance (null if not initialized)
 */
function getPurchasesInstance(): Purchases | null {
  return purchasesInstance;
}

/**
 * Change user (login/identify a user with RevenueCat)
 * Call this after Firebase authentication
 */
export async function identifyUser(userId: string): Promise<any> {
  const instance = getPurchasesInstance();
  if (!instance) return null;
  try {
    const customerInfo = await instance.changeUser(userId);
    return customerInfo;
  } catch (error) {
    console.error('Failed to identify user:', error);
    throw error;
  }
}

/**
 * Logout current user from RevenueCat
 * Call this when user logs out of Firebase
 */
export async function logoutUser(): Promise<void> {
  const instance = getPurchasesInstance();
  if (!instance) return;
  try {
    await instance.changeUser('$RCAnonymousID:' + Math.random().toString(36).substring(7));
  } catch (error) {
    console.error('Failed to logout user:', error);
  }
}

/**
 * Get current customer info
 * Returns subscription status, entitlements, and more
 */
export async function getCustomerInfo(): Promise<any> {
  const instance = getPurchasesInstance();
  if (!instance) return null;
  try {
    const customerInfo = await instance.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error('Failed to get customer info:', error);
    throw error;
  }
}

/**
 * Check if user has OPMODE Pro entitlement
 * This is the main function to check subscription status
 */
export async function hasProAccess(): Promise<boolean> {
  try {
    const customerInfo = await getCustomerInfo();
    if (!customerInfo) return false;
    const entitlement = customerInfo.entitlements?.active?.[ENTITLEMENT_ID];
    return !!(entitlement && entitlement.isActive);
  } catch (error) {
    return false;
  }
}

/**
 * Get detailed subscription status
 */
export async function getSubscriptionStatus(): Promise<{
  isPro: boolean;
  isActive: boolean;
  willRenew: boolean;
  expirationDate: Date | null;
  productIdentifier: string | null;
  isTrial: boolean;
}> {
  try {
    const customerInfo = await getCustomerInfo();
    if (!customerInfo) return NO_SUB;
    const entitlement = customerInfo.entitlements?.active?.[ENTITLEMENT_ID];

    if (!entitlement) return NO_SUB;

    return {
      isPro: true,
      isActive: entitlement.isActive,
      willRenew: entitlement.willRenew,
      expirationDate: entitlement.expirationDate ? new Date(entitlement.expirationDate) : null,
      productIdentifier: entitlement.productIdentifier,
      isTrial: entitlement.periodType === 'trial',
    };
  } catch (error) {
    return NO_SUB;
  }
}

/**
 * Get available offerings (subscription packages)
 */
export async function getOfferings(): Promise<any> {
  const instance = getPurchasesInstance();
  if (!instance) return null;
  try {
    const offerings = await instance.getOfferings();
    return offerings?.current ? offerings : null;
  } catch (error) {
    console.error('Failed to get offerings:', error);
    return null;
  }
}

/**
 * Purchase a package
 */
export async function purchasePackage(pkg: any): Promise<{
  success: boolean;
  customerInfo?: any;
  error?: string;
}> {
  const instance = getPurchasesInstance();
  if (!instance) {
    return { success: false, error: 'RevenueCat not initialized' };
  }
  try {
    const result = await instance.purchase({ rcPackage: pkg });
    return {
      success: true,
      customerInfo: result.customerInfo,
    };
  } catch (error: any) {
    console.error('Purchase failed:', error);

    // Handle specific error cases
    if (error?.userCancelled) {
      return {
        success: false,
        error: 'Purchase cancelled by user',
      };
    }

    return {
      success: false,
      error: error?.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Restore purchases (for Web SDK, this just refreshes customer info)
 * The Web SDK automatically syncs purchases from the backend
 */
export async function restorePurchases(): Promise<{
  success: boolean;
  customerInfo?: any;
  error?: string;
}> {
  try {
    const customerInfo = await getCustomerInfo();
    return {
      success: true,
      customerInfo,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to sync purchases',
    };
  }
}

/**
 * Get customer info with entitlement details
 * Useful for displaying subscription details in UI
 */
export async function getSubscriptionDetails(): Promise<{
  hasAccess: boolean;
  subscriptionType: 'monthly' | 'yearly' | 'none';
  expirationDate: string | null;
  willRenew: boolean;
  isInTrial: boolean;
  managementURL: string | null;
}> {
  try {
    const customerInfo = await getCustomerInfo();
    if (!customerInfo) return NO_DETAILS;
    const entitlement = customerInfo.entitlements?.active?.[ENTITLEMENT_ID];

    if (!entitlement) return NO_DETAILS;

    // Determine subscription type from the product identifier (handles Apple
    // '002'/'003', Play, and Web-Billing 'MonthlySub'/'YearlySub' — see helper).
    const subscriptionType = planTypeFromProductId(entitlement.productIdentifier);

    return {
      hasAccess: true,
      subscriptionType,
      expirationDate: entitlement.expirationDate || null,
      willRenew: entitlement.willRenew,
      isInTrial: entitlement.periodType === 'trial',
      managementURL: customerInfo.managementURL || null,
    };
  } catch (error) {
    return NO_DETAILS;
  }
}

/**
 * Check if SDK is initialized
 */
export function isRevenueCatInitialized(): boolean {
  return purchasesInstance !== null;
}

/**
 * Detect a recently-lapsed trial. Returns information about the user's
 * most recent expired entitlement so the app can surface a win-back
 * banner without requiring server-side webhook plumbing. Reads
 * `customerInfo.entitlements.all` (active + inactive) and finds the
 * most recent expirationDate that's now in the past.
 */
export async function getLapsedEntitlementInfo(): Promise<{
  hadEntitlement: boolean;
  daysSinceExpired: number | null;
  productIdentifier: string | null;
  /** 'monthly' | 'yearly' | 'none' — same shape as getSubscriptionDetails */
  subscriptionType: 'monthly' | 'yearly' | 'none';
}> {
  const NONE = { hadEntitlement: false, daysSinceExpired: null, productIdentifier: null, subscriptionType: 'none' as const };
  try {
    const customerInfo = await getCustomerInfo();
    if (!customerInfo) return NONE;

    // Skip if user is currently entitled — they're not lapsed.
    if (customerInfo.entitlements?.active?.[ENTITLEMENT_ID]) return NONE;

    // entitlements.all includes inactive ones. Each has an expirationDate
    // ISO string. We want the most recent past expiration.
    const all = customerInfo.entitlements?.all ?? {};
    const ent = all[ENTITLEMENT_ID];
    if (!ent || !ent.expirationDate) return NONE;

    const expiredAt = new Date(ent.expirationDate).getTime();
    if (isNaN(expiredAt)) return NONE;

    const now = Date.now();
    if (expiredAt > now) return NONE; // not actually expired

    const daysSinceExpired = Math.floor((now - expiredAt) / (1000 * 60 * 60 * 24));

    const pid = ent.productIdentifier || '';
    const subscriptionType = planTypeFromProductId(pid);

    return {
      hadEntitlement: true,
      daysSinceExpired,
      productIdentifier: pid || null,
      subscriptionType,
    };
  } catch {
    return NONE;
  }
}
